import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCampaign } from '../context/CampaignContext';
import { MainLayout } from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Swords, Plus, Users, Eye, EyeOff, Skull, MonitorPlay, Link2, Copy, Check, ExternalLink } from 'lucide-react';
import { isGameMaster } from '../lib/utils';
import { buildCombatantFromCreature } from '../lib/creatureStats';
import { toast } from 'sonner';

// Import combat components
import {
  EncounterList,
  CombatToolbar,
  CombatLog,
  CombatantCard,
  AddCombatantDialogs,
  CreateEncounterDialog
} from '../components/combat';

const API_URL = '/api';

export default function CombatPage() {
  const { campaignId } = useParams();
  const { user, language } = useAuth();
  const { currentCampaign } = useCampaign();
  
  // Data states
  const [encounters, setEncounters] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [players, setPlayers] = useState([]);
  const [npcs, setNpcs] = useState([]);
  const [creatures, setCreatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEncounter, setSelectedEncounter] = useState(null);
  
  // UI states
  const [createOpen, setCreateOpen] = useState(false);
  const [combatLogOpen, setCombatLogOpen] = useState(false);
  const [combatLog, setCombatLog] = useState([]);
  const [showDead, setShowDead] = useState(false);
  const [playerViewDialogOpen, setPlayerViewDialogOpen] = useState(false);
  const [combatPlayerViewToken, setCombatPlayerViewToken] = useState(null);
  const [activePlayerEncounterId, setActivePlayerEncounterId] = useState(null);
  const [activePlayerEncounterName, setActivePlayerEncounterName] = useState('');
  const [tokenCopied, setTokenCopied] = useState(false);
  
  // Search states for monsters
  const [monsterSearch, setMonsterSearch] = useState('');
  const [monsterResults, setMonsterResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  
  // Form state
  const [newEncounter, setNewEncounter] = useState({ 
    name: '', description: '', location: '',
    hp_display_mode: 'exact', auto_reroll_initiative: false, from_template_id: ''
  });

  const userRole = currentCampaign?.members?.find(m => m.user_id === user?.id)?.role;
  const isMJ = isGameMaster(userRole);

  const applyCombatPlayerViewState = useCallback((state) => {
    setCombatPlayerViewToken(state?.token || null);
    setActivePlayerEncounterId(state?.active_encounter_id || null);
    setActivePlayerEncounterName(state?.active_encounter_name || '');
  }, []);

  const fetchCombatPlayerViewState = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/campaigns/${campaignId}/combat/player-view/token`);
      applyCombatPlayerViewState(response.data);
    } catch (error) {
      setCombatPlayerViewToken(null);
      setActivePlayerEncounterId(null);
      setActivePlayerEncounterName('');
    }
  }, [applyCombatPlayerViewState, campaignId]);

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [encountersRes, npcsRes, templatesRes, creaturesRes] = await Promise.all([
        axios.get(`${API_URL}/campaigns/${campaignId}/encounters`),
        axios.get(`${API_URL}/campaigns/${campaignId}/npcs`),
        axios.get(`${API_URL}/campaigns/${campaignId}/templates`),
        axios.get(`${API_URL}/campaigns/${campaignId}/lore/search`, { params: { article_type: 'creature' } })
      ]);
      
      setEncounters(encountersRes.data);
      setNpcs(npcsRes.data);
      setTemplates(templatesRes.data);
      setCreatures(creaturesRes.data);
      
      if (currentCampaign?.members) {
        setPlayers(currentCampaign.members.filter(m => m.role !== 'owner'));
      }

      if (isMJ) {
        await fetchCombatPlayerViewState();
      }
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur de chargement' : 'Loading error');
    } finally {
      setLoading(false);
    }
  }, [campaignId, language, currentCampaign, fetchCombatPlayerViewState, isMJ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const generateCombatPlayerViewToken = async () => {
    try {
      const response = await axios.post(`${API_URL}/campaigns/${campaignId}/combat/player-view/token`);
      applyCombatPlayerViewState(response.data);
      toast.success(language === 'fr' ? 'Lien joueur genere' : 'Player link generated');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  const setDisplayedEncounterForPlayers = async (encounterId) => {
    try {
      const response = await axios.post(`${API_URL}/campaigns/${campaignId}/combat/player-view/display`, {
        encounter_id: encounterId || null
      });
      applyCombatPlayerViewState(response.data);
      const encounterName = response.data?.active_encounter_name || '';
      if (encounterName) {
        toast.success(language === 'fr' ? `Affichée: ${encounterName}` : `Displayed: ${encounterName}`);
      } else {
        toast.success(language === 'fr' ? 'Affichage joueur retire' : 'Player display cleared');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error'));
    }
  };

  const copyCombatPlayerViewUrl = () => {
    if (!combatPlayerViewToken) return;
    const url = `${window.location.origin}/player-view/combat/${combatPlayerViewToken}`;
    navigator.clipboard.writeText(url);
    setTokenCopied(true);
    setTimeout(() => setTokenCopied(false), 2000);
    toast.success(language === 'fr' ? 'Lien copie' : 'Link copied');
  };

  const openCombatPlayerView = () => {
    if (!combatPlayerViewToken) return;
    window.open(`${window.location.origin}/player-view/combat/${combatPlayerViewToken}`, '_blank', 'noopener,noreferrer');
  };

  const refreshEncounter = useCallback(async () => {
    if (!selectedEncounter) return;
    try {
      const response = await axios.get(`${API_URL}/encounters/${selectedEncounter.id}`);
      setSelectedEncounter(response.data);
      setEncounters(prev => prev.map(e => e.id === selectedEncounter.id ? response.data : e));
    } catch (error) {
      console.error('Failed to refresh encounter');
    }
  }, [selectedEncounter]);

  const addLogEntry = (message, type = 'info') => {
    setCombatLog(prev => [...prev, {
      id: Date.now(),
      timestamp: new Date().toLocaleTimeString(),
      message,
      type,
      round: selectedEncounter?.current_round || 0
    }]);
  };

  // Monster search
  const handleSearchMonsters = async () => {
    if (!monsterSearch.trim()) return;
    setSearchLoading(true);
    try {
      const response = await axios.get(`${API_URL}/monsters/search?q=${encodeURIComponent(monsterSearch)}`);
      setMonsterResults(response.data.results || []);
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur de recherche' : 'Search error');
    } finally {
      setSearchLoading(false);
    }
  };

  // Add monster from SRD
  const handleAddMonsterFromSearch = async (monsterIndex) => {
    try {
      const response = await axios.get(`${API_URL}/monsters/${monsterIndex}`);
      const monster = response.data;
      const ac = monster.armor_class?.[0]?.value || 10;
      const dexMod = Math.floor((monster.dexterity - 10) / 2);
      
      if (selectedEncounter) {
        const combatantData = {
          name: monster.name,
          combatant_type: 'monster',
          initiative: 0,
          initiative_modifier: dexMod,
          ac: ac,
          max_hp: monster.hit_points,
          current_hp: monster.hit_points,
          temp_hp: 0,
          speed: monster.speed?.walk || '30 ft',
          cr: String(monster.challenge_rating),
          xp: monster.xp || 0,
          monster_id: monster.index,
          aidedd_url: monster.aidedd_url,
          str_score: monster.strength,
          dex_score: monster.dexterity,
          con_score: monster.constitution,
          int_score: monster.intelligence,
          wis_score: monster.wisdom,
          cha_score: monster.charisma
        };
        
        await axios.post(`${API_URL}/encounters/${selectedEncounter.id}/combatants`, combatantData);
        await refreshEncounter();
        addLogEntry(`${monster.name} ajouté au combat (Init ${dexMod >= 0 ? '+' : ''}${dexMod})`, 'add');
        toast.success(language === 'fr' ? `${monster.name} ajouté !` : `${monster.name} added!`);
      }
      
      setMonsterSearch('');
      setMonsterResults([]);
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  // Add player from campaign
  const handleAddPlayer = async (member) => {
    if (!selectedEncounter) return;
    
    const combatantData = {
      name: member.character_name || member.username || 'Joueur',
      combatant_type: 'player',
      initiative: 0,
      initiative_modifier: 0,
      ac: 10,
      max_hp: 20,
      current_hp: 20,
      linked_player_id: member.user_id
    };
    
    try {
      await axios.post(`${API_URL}/encounters/${selectedEncounter.id}/combatants`, combatantData);
      await refreshEncounter();
      addLogEntry(`${combatantData.name} (Joueur) ajouté`, 'add');
      toast.success(language === 'fr' ? 'Joueur ajouté !' : 'Player added!');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  // Add NPC from campaign
  const handleAddNPC = async (npc) => {
    if (!selectedEncounter) return;
    
    const combatantData = {
      name: npc.name,
      combatant_type: 'npc',
      initiative: 0,
      initiative_modifier: 0,
      ac: 10,
      max_hp: 15,
      current_hp: 15,
      linked_npc_id: npc.id
    };
    
    try {
      await axios.post(`${API_URL}/encounters/${selectedEncounter.id}/combatants`, combatantData);
      await refreshEncounter();
      addLogEntry(`${npc.name} (PNJ) ajouté`, 'add');
      toast.success(language === 'fr' ? 'PNJ ajouté !' : 'NPC added!');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  // Add a canonical creature from the campaign encyclopedia. Combat values are
  // copied as a snapshot while the source id remains linked for traceability.
  const handleAddCreature = async (creature) => {
    if (!selectedEncounter) return;

    const combatantData = buildCombatantFromCreature(creature, language);

    try {
      await axios.post(`${API_URL}/encounters/${selectedEncounter.id}/combatants`, combatantData);
      await refreshEncounter();
      addLogEntry(`${creature.title} (${language === 'fr' ? 'Créature' : 'Creature'}) ajouté`, 'add');
      toast.success(language === 'fr' ? 'Créature ajoutée !' : 'Creature added!');
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error'));
    }
  };

  // Custom combatant
  const handleAddCustomCombatant = async (combatant) => {
    if (!selectedEncounter || !combatant.name) return;
    
    try {
      await axios.post(`${API_URL}/encounters/${selectedEncounter.id}/combatants`, combatant);
      await refreshEncounter();
      addLogEntry(`${combatant.name} ajouté`, 'add');
      toast.success(language === 'fr' ? 'Combattant ajouté !' : 'Combatant added!');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  // Encounter CRUD
  const handleCreateEncounter = async () => {
    try {
      const payload = {
        ...newEncounter,
        from_template_id: newEncounter.from_template_id || null
      };
      const response = await axios.post(`${API_URL}/campaigns/${campaignId}/encounters`, payload);
      setEncounters(prev => [response.data, ...prev]);
      setSelectedEncounter(response.data);
      setCreateOpen(false);
      setNewEncounter({ name: '', description: '', location: '', hp_display_mode: 'exact', auto_reroll_initiative: false, from_template_id: '' });
      setCombatLog([]);
      toast.success(language === 'fr' ? 'Rencontre créée !' : 'Encounter created!');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  const handleDeleteEncounter = async (encounterId) => {
    if (!window.confirm(language === 'fr' ? 'Supprimer cette rencontre ?' : 'Delete this encounter?')) return;
    try {
      await axios.delete(`${API_URL}/encounters/${encounterId}`);
      setEncounters(prev => prev.filter(e => e.id !== encounterId));
      if (selectedEncounter?.id === encounterId) {
        setSelectedEncounter(null);
        setCombatLog([]);
      }
      await fetchCombatPlayerViewState();
      toast.success(language === 'fr' ? 'Rencontre supprimée' : 'Encounter deleted');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  // Update encounter settings
  const handleUpdateEncounterSettings = async (settings) => {
    if (!selectedEncounter) return;
    try {
      await axios.patch(`${API_URL}/encounters/${selectedEncounter.id}`, settings);
      await refreshEncounter();
      toast.success(language === 'fr' ? 'Paramètres sauvegardés' : 'Settings saved');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  // Save as template
  const handleSaveAsTemplate = async (templateData) => {
    if (!selectedEncounter) return;
    try {
      const response = await axios.post(`${API_URL}/encounters/${selectedEncounter.id}/save-as-template`, templateData);
      setTemplates(prev => [response.data, ...prev]);
      toast.success(language === 'fr' ? 'Template créé !' : 'Template created!');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  // Combat controls
  const handleRollInitiatives = async () => {
    if (!selectedEncounter) return;
    try {
      const response = await axios.post(`${API_URL}/encounters/${selectedEncounter.id}/roll-initiative`);
      await refreshEncounter();
      response.data.initiatives?.forEach(init => {
        addLogEntry(`${init.name}: ${init.roll} + ${init.modifier} = ${init.total}`, 'roll');
      });
      toast.success(language === 'fr' ? 'Initiatives lancées !' : 'Initiatives rolled!');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  const handleStartCombat = async () => {
    if (!selectedEncounter) return;
    try {
      const response = await axios.post(`${API_URL}/encounters/${selectedEncounter.id}/start`);
      await refreshEncounter();
      response.data.initiatives?.forEach(init => {
        addLogEntry(`${init.name}: ${init.roll} + ${init.modifier} = ${init.total}`, 'roll');
      });
      await fetchCombatPlayerViewState();
      addLogEntry('⚔️ Combat démarré !', 'combat');
      toast.success(language === 'fr' ? 'Combat démarré !' : 'Combat started!');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  const handleNextTurn = async () => {
    if (!selectedEncounter) return;
    try {
      const response = await axios.post(`${API_URL}/encounters/${selectedEncounter.id}/next-turn`);
      await refreshEncounter();
      addLogEntry(`Tour de ${response.data.current_combatant}`, 'turn');
      toast.info(response.data.current_combatant, { description: `Round ${response.data.round}` });

      // Notify expired conditions
      const expired = response.data.expired_effects || [];
      for (const exp of expired) {
        const condLabel = exp.effect_name;
        toast.warning(`${exp.combatant_name}`, {
          description: language === 'fr'
            ? `Condition "${condLabel}" expirée`
            : `Condition "${condLabel}" expired`,
        });
        addLogEntry(
          language === 'fr'
            ? `${exp.combatant_name}: ${condLabel} expiré`
            : `${exp.combatant_name}: ${condLabel} expired`,
          'condition'
        );
      }
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  const handleEndCombat = async () => {
    if (!selectedEncounter) return;
    try {
      await axios.post(`${API_URL}/encounters/${selectedEncounter.id}/end`);
      await refreshEncounter();
      await fetchCombatPlayerViewState();
      addLogEntry('🏁 Combat terminé', 'combat');
      toast.success(language === 'fr' ? 'Combat terminé !' : 'Combat ended!');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  // Damage/Heal with automatic status update
  const handleApplyDamage = async (combatantId, amount, isHeal = false) => {
    if (!amount || isNaN(parseInt(amount))) return;
    
    const combatant = selectedEncounter?.combatants?.find(c => c.id === combatantId);
    if (!combatant) return;
    
    const dmg = parseInt(amount);
    let newHp;
    let newStatus = combatant.status || 'active';
    
    if (isHeal) {
      newHp = Math.min(combatant.max_hp, combatant.current_hp + dmg);
      addLogEntry(`${combatant.name} soigné de ${dmg} PV → ${newHp} PV`, 'heal');
      
      // If healed back from 0 or below, update status
      if (combatant.current_hp <= 0 && newHp > 0) {
        newStatus = 'active';
        addLogEntry(`${combatant.name} reprend conscience !`, 'heal');
      }
    } else {
      let remainingDmg = dmg;
      let newTempHp = combatant.temp_hp || 0;
      
      if (newTempHp > 0) {
        if (remainingDmg <= newTempHp) {
          newTempHp -= remainingDmg;
          remainingDmg = 0;
        } else {
          remainingDmg -= newTempHp;
          newTempHp = 0;
        }
      }
      
      // Allow negative HP for house rules
      newHp = combatant.current_hp - remainingDmg;
      addLogEntry(`${combatant.name} subit ${dmg} dégâts → ${newHp} PV`, 'damage');
      
      if (newTempHp !== combatant.temp_hp) {
        await axios.patch(`${API_URL}/combatants/${combatantId}`, { temp_hp: newTempHp });
      }
      
      // Automatic status update at 0 HP
      if (newHp <= 0 && combatant.status !== 'dead') {
        if (combatant.combatant_type === 'monster' || combatant.combatant_type === 'npc') {
          newStatus = 'dead';
          addLogEntry(`💀 ${combatant.name} est mort !`, 'death');
        } else if (combatant.combatant_type === 'player') {
          newStatus = 'unconscious';
          addLogEntry(`⚠️ ${combatant.name} tombe inconscient !`, 'damage');
        }
      }
    }
    
    try {
      const updateData = { current_hp: newHp };
      if (newStatus !== combatant.status) {
        updateData.status = newStatus;
      }
      await axios.patch(`${API_URL}/combatants/${combatantId}`, updateData);
      await refreshEncounter();
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  // Update combatant initiative
  const handleUpdateInitiative = async (combatantId, initiative) => {
    try {
      await axios.patch(`${API_URL}/combatants/${combatantId}`, { initiative: parseInt(initiative) || 0 });
      await refreshEncounter();
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  // Update initiative modifier
  const handleUpdateInitiativeModifier = async (combatantId, modifier) => {
    const combatant = selectedEncounter?.combatants?.find(c => c.id === combatantId);
    try {
      await axios.patch(`${API_URL}/combatants/${combatantId}`, { initiative_modifier: modifier });
      
      // Save to player profile if it's a player
      if (combatant?.linked_player_id) {
        // This would save to user/player profile - for now just update combatant
        addLogEntry(`Mod. initiative de ${combatant.name} mis à jour: ${modifier >= 0 ? '+' : ''}${modifier}`, 'info');
      }
      
      await refreshEncounter();
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  // Update max HP (for monsters)
  const handleUpdateMaxHp = async (combatantId, newMaxHp) => {
    const combatant = selectedEncounter?.combatants?.find(c => c.id === combatantId);
    if (!combatant) return;
    
    try {
      // Adjust current HP if it exceeds new max
      const newCurrentHp = Math.min(combatant.current_hp, newMaxHp);
      await axios.patch(`${API_URL}/combatants/${combatantId}`, { 
        max_hp: newMaxHp,
        current_hp: newCurrentHp 
      });
      addLogEntry(`PV max de ${combatant.name} modifiés: ${newMaxHp}`, 'info');
      await refreshEncounter();
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  // Toggle condition (duration: null = permanent, number = rounds)
  const handleToggleCondition = async (combatantId, conditionId, duration = null) => {
    const combatant = selectedEncounter?.combatants?.find(c => c.id === combatantId);
    if (!combatant) return;

    const currentEffects = combatant.status_effects || [];
    const hasCondition = currentEffects.some(e => e.name === conditionId);

    let newEffects;
    if (hasCondition) {
      newEffects = currentEffects.filter(e => e.name !== conditionId);
      addLogEntry(`${combatant.name}: ${conditionId} retiré`, 'condition');
    } else {
      newEffects = [...currentEffects, { name: conditionId, duration, description: '', source: '' }];
      const durText = duration ? ` (${duration} rounds)` : '';
      addLogEntry(`${combatant.name}: ${conditionId} appliqué${durText}`, 'condition');
    }

    try {
      await axios.patch(`${API_URL}/combatants/${combatantId}`, { status_effects: newEffects });
      await refreshEncounter();
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  // Update spell slots / resources
  const handleUpdateResources = async (combatantId, data) => {
    try {
      await axios.patch(`${API_URL}/combatants/${combatantId}`, data);
      await refreshEncounter();
    } catch {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  // Remove combatant
  const handleRemoveCombatant = async (combatantId) => {
    const combatant = selectedEncounter?.combatants?.find(c => c.id === combatantId);
    try {
      await axios.delete(`${API_URL}/combatants/${combatantId}`);
      await refreshEncounter();
      addLogEntry(`${combatant?.name || 'Combattant'} retiré`, 'remove');
      toast.success(language === 'fr' ? 'Combattant retiré' : 'Combatant removed');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  // Declare definitive death (for players)
  const handleDeclareDeath = async (combatantId) => {
    const combatant = selectedEncounter?.combatants?.find(c => c.id === combatantId);
    if (!combatant) return;
    
    try {
      await axios.patch(`${API_URL}/combatants/${combatantId}`, { status: 'dead' });
      addLogEntry(`💀 ${combatant.name} est définitivement mort(e).`, 'death');
      await refreshEncounter();
      toast.info(language === 'fr' ? `${combatant.name} est mort(e)` : `${combatant.name} has died`);
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  // Get combatants already in encounter to filter out
  const combatantPlayerIds = useMemo(() => 
    selectedEncounter?.combatants?.filter(c => c.linked_player_id).map(c => c.linked_player_id) || [],
    [selectedEncounter]
  );
  
  const combatantNpcIds = useMemo(() => 
    selectedEncounter?.combatants?.filter(c => c.linked_npc_id).map(c => c.linked_npc_id) || [],
    [selectedEncounter]
  );

  // Filter combatants to show (exclude dead if showDead is false)
  const visibleCombatants = useMemo(() => {
    if (!selectedEncounter?.combatants) return [];
    if (showDead) return selectedEncounter.combatants;
    return selectedEncounter.combatants.filter(c => c.status !== 'dead');
  }, [selectedEncounter?.combatants, showDead]);

  const currentTurnCombatantId = useMemo(() => {
    if (!selectedEncounter?.combatants?.length || selectedEncounter.status !== 'active') return null;
    const activeTurnOrder = selectedEncounter.combatants.filter(c => c.status !== 'dead');
    const current = activeTurnOrder[selectedEncounter.current_turn_index];
    return current?.id || null;
  }, [selectedEncounter]);

  // Count dead combatants
  const deadCount = useMemo(() => 
    selectedEncounter?.combatants?.filter(c => c.status === 'dead').length || 0,
    [selectedEncounter?.combatants]
  );

  const activeEncounters = useMemo(
    () => encounters.filter((encounter) => encounter.status === 'active' || encounter.status === 'paused'),
    [encounters]
  );

  if (!isMJ) {
    return (
      <MainLayout>
        <Card className="glass-card">
          <CardContent className="py-16 text-center">
            <Swords className="h-16 w-16 mx-auto text-muted-foreground mb-6" />
            <h3 className="font-cinzel text-xl font-bold mb-2">
              {language === 'fr' ? 'Accès réservé au MJ' : 'GM only access'}
            </h3>
          </CardContent>
        </Card>
      </MainLayout>
    );
  }

  return (
    <MainLayout fluid>
      <div className="flex h-[calc(100vh-4rem)]" data-testid="combat-page">
        {/* Left Panel - Encounters List */}
        <EncounterList
          encounters={encounters}
          selectedEncounter={selectedEncounter}
          loading={loading}
          language={language}
          onSelectEncounter={(enc) => { setSelectedEncounter(enc); setCombatLog([]); }}
          onCreateClick={() => setCreateOpen(true)}
          onOpenPlayerView={async () => {
            setPlayerViewDialogOpen(true);
            await fetchCombatPlayerViewState();
          }}
          onSetPlayerViewEncounter={setDisplayedEncounterForPlayers}
          activePlayerEncounterId={activePlayerEncounterId}
          activePlayerEncounterName={activePlayerEncounterName}
        />

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0">
          {selectedEncounter ? (
            <>
              {/* Toolbar */}
              <CombatToolbar
                encounter={selectedEncounter}
                language={language}
                onRollInitiatives={handleRollInitiatives}
                onStartCombat={handleStartCombat}
                onNextTurn={handleNextTurn}
                onEndCombat={handleEndCombat}
                onUpdateSettings={handleUpdateEncounterSettings}
                onSaveAsTemplate={handleSaveAsTemplate}
                onDeleteEncounter={handleDeleteEncounter}
                onToggleCombatLog={() => setCombatLogOpen(!combatLogOpen)}
                combatLogOpen={combatLogOpen}
              />

              {/* Combat Area */}
              <div className="flex-1 flex overflow-hidden">
                {/* Initiative Tracker */}
                <div className="flex-1 overflow-auto p-4">
                  {/* Add Combatant Buttons & Show Dead Toggle */}
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                    {selectedEncounter.status === 'preparing' ? (
                      <AddCombatantDialogs
                        language={language}
                        players={players}
                        npcs={npcs}
                        creatures={creatures}
                        combatantPlayerIds={combatantPlayerIds}
                        combatantNpcIds={combatantNpcIds}
                        monsterSearch={monsterSearch}
                        monsterResults={monsterResults}
                        searchLoading={searchLoading}
                        onAddPlayer={handleAddPlayer}
                        onAddNPC={handleAddNPC}
                        onAddCreature={handleAddCreature}
                        onAddCustomCombatant={handleAddCustomCombatant}
                        onMonsterSearchChange={setMonsterSearch}
                        onSearchMonsters={handleSearchMonsters}
                        onAddMonsterFromSearch={handleAddMonsterFromSearch}
                      />
                    ) : (
                      <div />
                    )}
                    
                    {/* Show Dead Toggle */}
                    {deadCount > 0 && (
                      <div className="flex items-center gap-2 bg-secondary/50 px-3 py-1.5 rounded-lg">
                        <Switch
                          id="show-dead"
                          checked={showDead}
                          onCheckedChange={setShowDead}
                        />
                        <Label htmlFor="show-dead" className="text-sm cursor-pointer flex items-center gap-1">
                          {showDead ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                          {language === 'fr' ? `Morts (${deadCount})` : `Dead (${deadCount})`}
                        </Label>
                      </div>
                    )}
                  </div>

                  {/* Combatants List */}
                  <div className="space-y-2">
                    {selectedEncounter.combatants?.length === 0 ? (
                      <Card className="glass-card">
                        <CardContent className="py-12 text-center">
                          <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                          <p className="text-muted-foreground">
                            {language === 'fr' 
                              ? 'Ajoutez des combattants pour commencer'
                              : 'Add combatants to start'}
                          </p>
                        </CardContent>
                      </Card>
                    ) : visibleCombatants.length === 0 ? (
                      <Card className="glass-card">
                        <CardContent className="py-8 text-center">
                          <Skull className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                          <p className="text-muted-foreground text-sm">
                            {language === 'fr' 
                              ? 'Tous les combattants sont morts. Activez "Afficher les morts" pour les voir.'
                              : 'All combatants are dead. Enable "Show dead" to see them.'}
                          </p>
                        </CardContent>
                      </Card>
                    ) : (
                      visibleCombatants.map((combatant) => {
                        return (
                          <CombatantCard
                            key={combatant.id}
                            combatant={combatant}
                            encounter={selectedEncounter}
                            language={language}
                            isCurrentTurn={selectedEncounter.status === 'active' && currentTurnCombatantId === combatant.id}
                            showDead={showDead}
                            onUpdateInitiative={handleUpdateInitiative}
                            onUpdateInitiativeModifier={handleUpdateInitiativeModifier}
                            onApplyDamage={handleApplyDamage}
                            onUpdateMaxHp={handleUpdateMaxHp}
                            onToggleCondition={handleToggleCondition}
                            onRemove={handleRemoveCombatant}
                            onDeclareDeath={handleDeclareDeath}
                            onUpdateResources={handleUpdateResources}
                          />
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Combat Log Panel */}
                {combatLogOpen && (
                  <CombatLog
                    combatLog={combatLog}
                    language={language}
                    onClear={() => setCombatLog([])}
                  />
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Swords className="h-16 w-16 mx-auto text-muted-foreground mb-6" />
                <h3 className="font-cinzel text-xl font-bold mb-2">
                  {language === 'fr' ? 'Sélectionnez une rencontre' : 'Select an encounter'}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {language === 'fr' 
                    ? 'Ou créez-en une nouvelle pour commencer'
                    : 'Or create a new one to get started'}
                </p>
                <Button onClick={() => setCreateOpen(true)} className="fantasy-button">
                  <Plus className="mr-2 h-4 w-4" />
                  {language === 'fr' ? 'Nouvelle rencontre' : 'New encounter'}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Create Encounter Dialog */}
        <CreateEncounterDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          language={language}
          templates={templates}
          newEncounter={newEncounter}
          onEncounterChange={setNewEncounter}
          onCreate={handleCreateEncounter}
        />

        <Dialog open={playerViewDialogOpen} onOpenChange={setPlayerViewDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-cinzel flex items-center gap-2">
                <MonitorPlay className="h-5 w-5" />
                {language === 'fr' ? 'Vue joueur combat' : 'Combat player view'}
              </DialogTitle>
              <DialogDescription>
                {language === 'fr'
                  ? 'Lien persistant de campagne. Le combat affiche est synchronise automatiquement.'
                  : 'Persistent campaign link. Displayed combat is synchronized automatically.'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              {combatPlayerViewToken ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Input
                      readOnly
                      className="text-xs"
                      value={`${window.location.origin}/player-view/combat/${combatPlayerViewToken}`}
                    />
                    <Button type="button" size="icon" variant="outline" onClick={copyCombatPlayerViewUrl}>
                      {tokenCopied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                  <Button type="button" variant="outline" className="w-full gap-2" onClick={openCombatPlayerView}>
                    <ExternalLink className="h-4 w-4" />
                    {language === 'fr' ? 'Ouvrir la vue joueur' : 'Open player view'}
                  </Button>
                </div>
              ) : (
                <Button type="button" className="w-full gap-2" onClick={generateCombatPlayerViewToken}>
                  <Link2 className="h-4 w-4" />
                  {language === 'fr' ? 'Generer le lien persistant' : 'Generate persistent link'}
                </Button>
              )}

              <div className="rounded-lg border border-border/80 bg-secondary/20 p-3 text-sm">
                <p className="font-medium">{`affichée:${activePlayerEncounterName || '-'}`}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {language === 'fr'
                    ? 'Au demarrage d une rencontre, elle est affichee automatiquement. A la fin, elle disparait cote joueurs.'
                    : 'On encounter start, it is automatically displayed. On end, it is removed from player view.'}
                </p>
              </div>

              {activeEncounters.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">
                    {language === 'fr' ? 'Choisir le combat affiche' : 'Choose displayed combat'}
                  </p>
                  <div className="space-y-2 max-h-48 overflow-auto pr-1">
                    {activeEncounters.map((encounter) => (
                      <div key={encounter.id} className="flex items-center justify-between gap-2 rounded-md border p-2">
                        <div className="min-w-0">
                          <p className="text-sm truncate">{encounter.name}</p>
                          <p className="text-xs text-muted-foreground">Round {encounter.current_round || 1}</p>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant={activePlayerEncounterId === encounter.id ? 'secondary' : 'outline'}
                          onClick={() => setDisplayedEncounterForPlayers(encounter.id)}
                        >
                          {language === 'fr' ? 'Afficher' : 'Display'}
                        </Button>
                      </div>
                    ))}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full"
                    onClick={() => setDisplayedEncounterForPlayers(null)}
                  >
                    {language === 'fr' ? 'Retirer de l ecran joueur' : 'Clear player display'}
                  </Button>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPlayerViewDialogOpen(false)}>
                {language === 'fr' ? 'Fermer' : 'Close'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}

